<template>
  <v-card border class="h-100" rounded="lg">
    <v-sheet
      :color="statusCard.color"
      :style="{ borderBottomColor: statusCard.borderColor }"
      class="pa-4 task-status-sheet">
      <div
        :class="statusCard.titleClass"
        class="d-flex align-center ga-1 font-weight-bold">
        <v-icon :icon="statusCard.icon" />
        <span class="text-uppercase">{{ statusCard.title }}</span>
      </div>
      <div
        :class="[
          statusCard.mainClass,
          statusCard.isLarge ? 'font-size-14 font-weight-bold' : 'font-size-13',
        ]"
        class="mt-1">
        {{ statusCard.mainText }}
      </div>
      <div
        v-if="statusCard.subText"
        :class="statusCard.subClass"
        class="font-size-13">
        {{ statusCard.subText }}
      </div>
      <div
        v-if="statusCard.warningText"
        :class="statusCard.warningClass"
        class="font-size-13 font-weight-bold mt-1">
        {{ statusCard.warningText }}
      </div>
    </v-sheet>

    <v-divider />

    <v-card-text>
      <div class="label">Створено</div>
      <div class="font-size-13">
        {{ new CustomDate(task.created_at).toString({ time: true }) }}
      </div>

      <div class="mt-3 label">Автор</div>
      <div class="font-size-13">
        {{ getPersonName(task.author, { initials: true }) }}
      </div>
      <div class="text-grey-darken-1 font-size-13">
        {{ task.author?.post || task.author?.department?.name || '' }}
      </div>
    </v-card-text>

    <v-divider />

    <v-card-text>
      <div class="d-flex align-center justify-space-between">
        <div class="label">Термін виконання</div>
        <v-icon v-if="isDraftLocked" color="grey" icon="mdi-lock-outline" />
      </div>
      <b-input-date
        v-model="taskData.deadline"
        :error-messages="getErrorMessages(v$.deadline)"
        :loading="$loading.isLoading('updateDeadline')"
        :readonly="
          disabled ||
          $loading.isLoading('updateDeadline') ||
          taskData?.statusId !== 1
        "
        hide-details
        time-picker
        @blur="v$.deadline.$touch()"
        @update:model-value="updateDeadline" />
      <div v-if="isDraftLocked" class="mt-3 d-flex align-center ga-2 text-grey">
        <v-icon icon="mdi-lock-outline" />
        <span>Заблоковано: завдання вже не у статусі чернетки</span>
      </div>
    </v-card-text>

    <v-divider />

    <v-card-text v-if="taskData.typeId !== 2">
      <person-section
        :disabled="disabled || taskData.statusId !== 1"
        :loading="$loading.isLoading('updateExecutor')"
        :people="draftExecutor ? [draftExecutor] : []"
        action-label="Змінити"
        title="Виконавець">
        <template #action>
          <v-menu
            v-if="canEditAssignees"
            v-model="executorMenu"
            :close-on-content-click="false"
            location="bottom end">
            <template #activator="{ props: menuProps }">
              <span
                class="sidebar-action-link d-inline-flex align-center ga-1 cursor-pointer"
                v-bind="menuProps">
                <v-icon icon="mdi-pencil-outline" />
                Змінити
              </span>
            </template>
            <task-employee-select
              v-model="draftExecutorId"
              @select="executorMenu = false" />
          </v-menu>
        </template>
      </person-section>
    </v-card-text>

    <v-divider v-if="taskData.typeId !== 2" />

    <v-card-text>
      <person-section
        :disabled="disabled || taskData.statusId !== 1"
        :loading="$loading.isLoading('updateControlUsers')"
        :people="draftControlUsers"
        :show-info="controlUsersInfo"
        :title="`Контролюючі (${draftControlUsers.length})`"
        action-label="Додати"
        multiple
        @delete="removeDraftControlUser">
        <template #action>
          <v-menu
            v-if="canEditAssignees"
            v-model="controlMenu"
            :close-on-content-click="false"
            location="bottom end">
            <template #activator="{ props: menuProps }">
              <span
                class="sidebar-action-link d-inline-flex align-center ga-1 cursor-pointer"
                v-bind="menuProps">
                <v-icon icon="mdi-plus" />
                Додати
              </span>
            </template>
            <task-employee-select v-model="draftControlUserIds" multiple />
          </v-menu>
        </template>
      </person-section>
    </v-card-text>

    <v-divider />

    <v-card-text>
      <person-section
        :disabled="disabled || taskData.statusId !== 1"
        :loading="$loading.isLoading('updateObservers')"
        :people="draftObservers"
        :show-info="observersInfo"
        :title="`Наглядачі (${draftObservers.length})`"
        action-label="Додати"
        multiple
        @delete="removeDraftObserver">
        <template #action>
          <v-menu
            v-if="canEditAssignees"
            v-model="observerMenu"
            :close-on-content-click="false"
            location="bottom end">
            <template #activator="{ props: menuProps }">
              <span
                class="sidebar-action-link d-inline-flex align-center ga-1 cursor-pointer"
                v-bind="menuProps">
                <v-icon icon="mdi-plus" />
                Додати
              </span>
            </template>
            <task-employee-select v-model="draftObserverIds" multiple />
          </v-menu>
        </template>
      </person-section>
    </v-card-text>

    <slot v-if="$slots['action-buttons']" name="action-buttons"></slot>
  </v-card>
</template>

<script lang="ts">
import { CustomDate, getErrorMessages } from 'best-modules/utils'
import { getPersonName } from 'best-modules/utils/helpers'
import { computed, ref, watch } from 'vue'
import { useSidebar } from './sidebar'
import { BInputDate } from 'best-modules/components'
import PersonSection from './PersonSection.vue'
import TaskEmployeeSelect from './TaskEmployeeSelect.vue'
import { fillDirectory, getDirectory } from '@/directory'

export default {
  name: 'Sidebar',
  emits: ['update:task', 'update:assignee-draft'],
  components: {
    BInputDate,
    PersonSection,
    TaskEmployeeSelect,
  },
  props: {
    task: {
      type: Object,
      required: true,
    },
    v$: {
      type: Object,
      default: () => ({ $validate: () => false }),
    },
    disabled: {
      type: Boolean,
      defaults: false,
    },
  },
  methods: {
    getErrorMessages,
    getPersonName,
    CustomDate,
  },
  setup(props, { emit }) {
    const executorMenu = ref(false)
    const controlMenu = ref(false)
    const observerMenu = ref(false)
    const draftExecutorId = ref<any>(null)
    const draftControlUserIds = ref<any[]>([])
    const draftObserverIds = ref<any[]>([])
    const controlUsersInfo =
      'Контролюючі  — особи, які перевіряють якість виконання завдання. ' +
      'Зазвичай це безпосередній керівник виконавця або куратор проєкту, ' +
      'який приймає рішення про прийняття чи відхилення результату.'
    const observersInfo =
      'Наглядачі  — особи, які отримують інформаційні сповіщення про прогрес завдання. ' +
      'На відміну від контролюючих, не приймають рішень про прийняття роботи, ' +
      'але можуть слідкувати за статусом та надавати рекомендації.'

    fillDirectory('userList')

    const taskData = computed({
      get() {
        return props.task
      },
      set(val) {
        emit('update:task', val)
      },
    })

    const deadlineText = computed(() => {
      if (!props.task?.deadline) {
        return ''
      }

      return new CustomDate(props.task.deadline).toString({
        time: true,
        format: 'locale-hybrid',
      })
    })

    const isActiveOverdue = computed(() => {
      if (![1, 2].includes(props.task?.statusId) || !props.task?.deadline) {
        return false
      }

      if (props.task?.isOverdue) {
        return true
      }

      return new CustomDate(props.task.deadline).date.toDate() < new Date()
    })

    const isCompletedLate = computed(() => {
      return [3, 5].includes(props.task?.statusId) && props.task?.isOverdue
    })

    const completedDateText = computed(() => {
      if (!props.task?.completedDate) {
        return deadlineText.value
      }

      return new CustomDate(props.task.completedDate).toString({
        time: true,
        format: 'locale-hybrid',
      })
    })

    const statusCard = computed(() => {
      if (isActiveOverdue.value && props.task?.statusId === 1) {
        return {
          color: 'red-lighten-5',
          borderColor: 'rgb(var(--v-theme-error))',
          icon: 'mdi-alert-circle-outline',
          title: 'Завдання прострочене',
          titleClass: 'text-red-darken-2',
          mainText: deadlineText.value,
          mainClass: 'text-black',
          subText: '',
          subClass: '',
          warningText: '',
          warningClass: '',
          isLarge: true,
        }
      }

      if (isCompletedLate.value) {
        return {
          color: 'red-lighten-5',
          borderColor: 'rgb(var(--v-theme-error))',
          icon: 'mdi-alert-circle-outline',
          title: 'Завершено із запізненням',
          titleClass: 'text-red-darken-2',
          mainText: completedDateText.value,
          mainClass: 'text-black',
          subText: `Дедлайн: ${deadlineText.value}`,
          subClass: 'text-disabled font-weight-bold',
          warningText: '',
          warningClass: '',
          isLarge: true,
        }
      }

      if (props.task?.statusId === 1) {
        return {
          color: 'yellow-lighten-5',
          borderColor: 'rgb(var(--v-theme-warning))',
          icon: 'mdi-clock-outline',
          title: 'Очікує виконання',
          titleClass: 'text-yellow-darken-4',
          mainText: deadlineText.value,
          mainClass: 'text-black',
          subText: '',
          subClass: '',
          warningText: '',
          warningClass: '',
          isLarge: true,
        }
      }

      if (props.task?.statusId === 5) {
        return {
          color: 'orange-lighten-5',
          borderColor: 'rgb(var(--v-theme-warning))',
          icon: 'mdi-magnify',
          title: 'Завдання на перевірці',
          titleClass: 'text-orange-darken-3',
          mainText: 'Очікує рішення контролюючого',
          mainClass: 'text-black',
          subText: `Дедлайн: ${deadlineText.value}`,
          subClass: 'text-disabled font-weight-bold',
          warningText: '',
          warningClass: '',
          isLarge: false,
        }
      }

      return {
        color: 'blue-lighten-5',
        borderColor: 'rgb(var(--v-theme-secondary))',
        icon: 'mdi-play-outline',
        title: props.task?.status?.name
          ? `Завдання ${props.task.status.name.toLowerCase()}`
          : 'Завдання в роботі',
        titleClass: 'text-blue-darken-3',
        mainText: `Виконується ${getPersonName(props.task.executor, {
          initials: true,
        })}`,
        mainClass: 'text-black',
        subText: `Дедлайн: ${deadlineText.value}`,
        subClass: 'text-grey',
        warningText: isActiveOverdue.value ? 'Протерміновано' : '',
        warningClass: 'text-red-darken-2',
        isLarge: false,
      }
    })

    const isDraftLocked = computed(() => props.task?.statusId !== 1)
    const canEditAssignees = computed(
      () => !props.disabled && !isDraftLocked.value
    )

    const sidebar = useSidebar(props.task)

    const findUser = (userId: any) => {
      return getDirectory('userList').find(userItem => userItem.id === userId)
    }

    const createPersonItem = (userId: number, sourceList: any[] = []) => {
      const existing = sourceList.find(item => item.userId === userId)
      if (existing) return existing

      return findUser(userId) || { id: userId, userId, name: '' }
    }

    const draftExecutor = computed(() => {
      const currentExecutor = {
        ...props.task.executor,
        userId: props.task.executorId,
      }

      return draftExecutorId.value
        ? createPersonItem(draftExecutorId.value, [currentExecutor])
        : null
    })

    const draftControlUsers = computed(() =>
      draftControlUserIds.value.map(userId =>
        createPersonItem(userId, props.task.controlUsers || [])
      )
    )

    const draftObservers = computed(() =>
      draftObserverIds.value.map(userId =>
        createPersonItem(userId, props.task.observers || [])
      )
    )

    const syncDraftFromTask = () => {
      draftExecutorId.value = props.task.executorId || null
      draftControlUserIds.value =
        props.task.controlUsers?.map((item: any) => item.userId) || []
      draftObserverIds.value =
        props.task.observers?.map((item: any) => item.userId) || []
    }

    watch(
      () => props.task.id,
      () => syncDraftFromTask(),
      { immediate: true }
    )

    watch(
      [draftExecutorId, draftControlUserIds, draftObserverIds],
      () => {
        emit('update:assignee-draft', {
          executorId: draftExecutorId.value,
          controlUserIds: draftControlUserIds.value,
          observerIds: draftObserverIds.value,
        })
      },
      { deep: true, immediate: true }
    )

    const removeDraftControlUser = (personId: any) => {
      const person = draftControlUsers.value.find(item => item.id === personId)
      const userId = person?.userId || personId
      draftControlUserIds.value = draftControlUserIds.value.filter(
        id => id !== userId
      )
    }

    const removeDraftObserver = (personId: any) => {
      const person = draftObservers.value.find(item => item.id === personId)
      const userId = person?.userId || personId
      draftObserverIds.value = draftObserverIds.value.filter(
        id => id !== userId
      )
    }

    return {
      taskData,
      canEditAssignees,
      controlMenu,
      controlUsersInfo,
      draftControlUsers,
      draftControlUserIds,
      draftExecutor,
      draftExecutorId,
      draftObserverIds,
      draftObservers,
      executorMenu,
      isDraftLocked,
      observerMenu,
      observersInfo,
      removeDraftControlUser,
      removeDraftObserver,
      statusCard,
      ...sidebar,
    }
  },
}
</script>

<style lang="scss" scoped>
.task-status-sheet {
  border-bottom: 1px solid;
}

.sidebar-action-link {
  color: rgb(var(--v-theme-secondary));
  font-size: 12px;
  font-weight: 600;
}
</style>
